import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useForm, Controller } from 'react-hook-form';
import { landingColors } from '../../../theme/landingTheme';
import { MobileModal } from '../../../components/mobile/MobileModal';
import { MobileFormField } from '../../../components/mobile/MobileFormField';

interface DemoRequestModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  storeCount: string;
  message: string;
}

const storeCountOptions = [
  { value: '1', label: '1 store' },
  { value: '2-5', label: '2-5 stores' },
  { value: '6-10', label: '6-10 stores' },
  { value: '11-25', label: '11-25 stores' },
  { value: '25+', label: '25+ stores' },
];

export const DemoRequestModal: React.FC<DemoRequestModalProps> = ({ open, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      businessName: '',
      contactName: '',
      email: '',
      phone: '',
      storeCount: '',
      message: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    console.log('Demo request submitted:', data);

    setIsSubmitting(false);
    setIsSuccess(true);

    // Reset and close after showing success
    setTimeout(() => {
      setIsSuccess(false);
      reset();
      onClose();
    }, 2500);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setIsSuccess(false);
      reset();
      onClose();
    }
  };

  return (
    <MobileModal
      open={open}
      onClose={handleClose}
      title="Request a Demo"
      maxWidth="sm"
    >
        {isSuccess ? (
          // Success State
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
              animation: 'fadeIn 0.3s ease',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'scale(0.95)' },
                to: { opacity: 1, transform: 'scale(1)' },
              },
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: 48,
                  color: landingColors.success,
                }}
              />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: landingColors.textPrimary,
                mb: 1,
              }}
            >
              Request Received!
            </Typography>
            <Typography
              sx={{
                color: landingColors.textSecondary,
              }}
            >
              We'll be in touch within 24 hours to schedule your demo.
            </Typography>
          </Box>
        ) : (
          // Form
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: landingColors.textSecondary,
                mb: 3,
              }}
            >
              Fill out the form below and our team will get back to you within 24
              hours to schedule a personalized demo.
            </Typography>

            <Grid container spacing={2}>
              {/* Business Name */}
              <Grid item xs={12}>
                <MobileFormField
                  name="businessName"
                  control={control}
                  rules={{ required: 'Business name is required' }}
                  label="Business Name"
                  placeholder="e.g., TechZone Electronics"
                  fullWidth
                  disabled={isSubmitting}
                />
              </Grid>

              {/* Contact Name */}
              <Grid item xs={12} sm={6}>
                <MobileFormField
                  name="contactName"
                  control={control}
                  rules={{ required: 'Contact name is required' }}
                  label="Your Name"
                  placeholder="John Doe"
                  fullWidth
                  disabled={isSubmitting}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} sm={6}>
                <MobileFormField
                  name="email"
                  control={control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  }}
                  label="Email Address"
                  placeholder="john@company.com"
                  type="email"
                  fullWidth
                  disabled={isSubmitting}
                />
              </Grid>

              {/* Phone */}
              <Grid item xs={12} sm={6}>
                <MobileFormField
                  name="phone"
                  control={control}
                  label="Phone (Optional)"
                  placeholder="+213 XXX XXX XXX"
                  type="tel"
                  fullWidth
                  disabled={isSubmitting}
                />
              </Grid>

              {/* Store Count */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="storeCount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      select
                      label="Number of Stores"
                      disabled={isSubmitting}
                    >
                      {storeCountOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              {/* Message */}
              <Grid item xs={12}>
                <Controller
                  name="message"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={3}
                      label="Message (Optional)"
                      placeholder="Tell us about your business and what you're looking for..."
                      disabled={isSubmitting}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitting}
              sx={{
                mt: 3,
                py: 1.75,
                background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.primaryDark} 100%)`,
                boxShadow: '0 8px 24px rgba(99, 102, 241, 0.35)',
                '&:hover': {
                  boxShadow: '0 12px 32px rgba(99, 102, 241, 0.45)',
                },
                '&:disabled': {
                  background: landingColors.border,
                },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              ) : (
                'Submit Request'
              )}
            </Button>

            {/* Privacy Note */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                mt: 2,
                color: landingColors.textMuted,
              }}
            >
              By submitting, you agree to our{' '}
              <Box
                component="a"
                href="#"
                sx={{
                  color: landingColors.primary,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Privacy Policy
              </Box>{' '}
              and{' '}
              <Box
                component="a"
                href="#"
                sx={{
                  color: landingColors.primary,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Terms of Service
              </Box>
              .
            </Typography>
          </Box>
        )}
    </MobileModal>
  );
};

export default DemoRequestModal;
